// Stateful controller around the Twilio Voice SDK Device.
// Lives outside the React tree so the Device instance can outlive component
// mounts during a call (the floating call card may unmount/remount as the user
// navigates while the call is still active). Drives the Redux slice via
// dispatched actions; components read state by selecting from the slice.
//
// Lifecycle: very-lazy — Device is constructed inside startCall and torn down
// in hangup / on terminal events. There is no module-level Device that lingers
// between calls.

import { Device } from '@twilio/voice-sdk';

import { store, dispatch } from 'store';
import {
    callFetchingToken,
    callConnecting,
    callRinging,
    callInProgress,
    callEnded,
    callRemoteDisconnect,
    callError,
    setMuted as setMutedAction,
    setAudioDevices,
    setSelectedInputDeviceId,
    setSelectedOutputDeviceId,
    setOutputSelectionSupported,
    generatedNoteUpdated,
    generatedNoteCleared,
    callCardDismissed
} from 'store/slices/twilioOutboundCall';
import { openSnackbar } from 'store/slices/snackbar';
import { fetchTwilioAccessToken, fetchGeneratedCareSessionNote } from 'api/twilio';
import logger from './logger';

// localStorage keys for the user's preferred audio devices. Persisted across
// calls so the next call reuses them without re-prompting. `deviceId` is stable
// per origin once mic permission is granted; if a saved ID is no longer in the
// available list (e.g. headset unplugged), we clear the saved value and fall
// back to the system default.
const LS_INPUT_DEVICE_KEY = 'synergrx.twilio.preferredInputDeviceId';
const LS_SPEAKER_DEVICE_KEY = 'synergrx.twilio.preferredSpeakerDeviceId';

function readPreferredDeviceId(key) {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

function writePreferredDeviceId(key, deviceId) {
    try {
        if (deviceId) {
            localStorage.setItem(key, deviceId);
        } else {
            localStorage.removeItem(key);
        }
    } catch (e) {
        // ignore — quota / privacy mode
    }
}

// Output routing (speakerDevices / ringtoneDevices) relies on HTMLMediaElement's
// setSinkId. Desktop coverage is now broad (Chrome/Edge, Firefox 116+, Safari
// 18.4+), but most mobile browsers still lack it. Detect at module load via
// feature-check rather than UA-sniffing so the picker hides the speaker
// dropdown wherever it isn't actually available.
const OUTPUT_SELECTION_SUPPORTED = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

/**
 * @typedef {{ deviceId: string, label: string }} AudioDeviceInfo
 */

// Twilio's `device.audio.availableInputDevices` / `availableOutputDevices` Maps
// are empty immediately after `new Device(...)` — they only populate after the
// SDK acquires a mic stream and the first internal deviceChange fires. The
// browser's enumerateDevices() is ready right away once mic permission has
// been granted (which it must be at this point, since we just constructed a
// Device). Drive the picker + saved-ID matching from here instead, otherwise
// the picker renders empty and saved preferences never match.
/**
 * @returns {Promise<{ inputs: AudioDeviceInfo[], outputs: AudioDeviceInfo[] }>}
 */
async function enumerateAudioDevices() {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
        return { inputs: [], outputs: [] };
    }
    try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const inputs = all.filter((d) => d.kind === 'audioinput').map((d) => ({ deviceId: d.deviceId, label: d.label || '' }));
        const outputs = all.filter((d) => d.kind === 'audiooutput').map((d) => ({ deviceId: d.deviceId, label: d.label || '' }));
        return { inputs, outputs };
    } catch (e) {
        return { inputs: [], outputs: [] };
    }
}

// Dev-only fake mode: drives the Redux slice through the call lifecycle on
// timers without touching Twilio. Lets the UI be exercised end-to-end without
// a Twilio account, real creds, or network access. Toggle via env:
//   REACT_APP_TWILIO_FAKE=1               enable
//   REACT_APP_TWILIO_FAKE_OUTCOME=...     answer (default) | ring | busy | noanswer | error
//   REACT_APP_TWILIO_FAKE_DELAY_MS=...    base delay between transitions (default 600)
const FAKE_ENABLED = process.env.REACT_APP_TWILIO_FAKE === '1';
const FAKE_OUTCOME = process.env.REACT_APP_TWILIO_FAKE_OUTCOME || 'answer';
const FAKE_DELAY_MS = Number.parseInt(process.env.REACT_APP_TWILIO_FAKE_DELAY_MS, 10) || 600;

/** @type {import('@twilio/voice-sdk').Device | null} */
let device = null;
/** @type {import('@twilio/voice-sdk').Call | null} */
let activeCall = null;
/** @type {(() => Promise<void>) | null} */
let deviceChangeHandler = null;
// Synchronous guard to close the gap between startCall being invoked and
// `device` being assigned. Prevents a second concurrent startCall (UI
// double-click, programmatic re-entry) from passing the `device || activeCall`
// check while the first call's token fetch is still in flight.
let starting = false;

// Per-call flags used to classify how a call ended. The Twilio JS SDK fires
// the same `disconnect` event whether we hung up locally or the remote party
// did, so we shadow it: hangup() flips localHangupRequested before triggering
// the SDK disconnect, and the call's `accept` event flips wasAccepted. Read in
// wireCallEvents to decide between callEnded (local, card vanishes) and
// callRemoteDisconnect (remote, card lingers with a label).
let localHangupRequested = false;
let wasAccepted = false;
// Set by hangup() when invoked while `starting` is true but no Call exists
// yet. The pending startCall checks this flag at each await checkpoint and
// tears down quietly rather than dispatching callConnecting / callError on
// top of the callEnded that hangup() already emitted.
let cancelDuringStart = false;

// Latest input volume (0..1) from the active call's `volume` event. The Twilio
// Voice SDK does not expose a polled volume on device.audio — it emits a
// `volume` event on the Call (~50Hz) once media is flowing. We cache the most
// recent value here and let the picker poll getInputVolume() against it.
let latestInputVolume = 0;

let fakeTimers = [];
let fakeActive = false;

// Generated-note polling. Module-scope, single-slot — survives the call card
// unmount on hangup. Back-to-back calls overwrite any prior pipeline still in
// flight; accepted edge case (pipelines usually settle in seconds).
const NOTE_POLL_INITIAL_DELAY_MS = 5 * 1000;
const NOTE_POLL_INTERVAL_MS = 1 * 1000;
const NOTE_POLL_MAX_ATTEMPTS = 5 * 60;
/** @type {{ callSid: string, clinicId: number, timeoutId: ReturnType<typeof setTimeout> | null, cancelled: boolean } | null} */
let activeNotePoller = null;

function stopNotePolling() {
    if (!activeNotePoller) {
        return;
    }
    logger.info('twilio:outboundcall stopping generated note polling', {
        callSid: activeNotePoller.callSid,
        clinicId: activeNotePoller.clinicId
    });
    activeNotePoller.cancelled = true;
    if (activeNotePoller.timeoutId) {
        clearTimeout(activeNotePoller.timeoutId);
    }
    activeNotePoller = null;
}

// Terminal: note settled either way, or transcript failed (no note will follow).
function isPipelineTerminal({ transcriptStatus, noteStatus }) {
    return noteStatus === 'READY' || noteStatus === 'FAILED' || noteStatus === 'SKIPPED_SHORT_TRANSCRIPT' || transcriptStatus === 'FAILED';
}

async function pollGeneratedNoteOnce(attempt) {
    const poller = activeNotePoller;
    if (!poller || poller.cancelled) {
        return;
    }
    try {
        const response = await fetchGeneratedCareSessionNote(poller.clinicId, poller.callSid);
        if (poller.cancelled || activeNotePoller !== poller) {
            return;
        }
        const data = response?.data ?? {};
        const transcriptStatus = data.transcriptStatus ?? null;
        const rawNoteStatus = data.noteStatus ?? null;
        const text = data.note ?? null;
        // READY-without-text shouldn't happen; coerce to FAILED so the indicator
        // doesn't get stuck on a "ready" state with nothing to append.
        const noteStatus = rawNoteStatus === 'READY' && !text ? 'FAILED' : rawNoteStatus;
        dispatch(generatedNoteUpdated({ transcriptStatus, noteStatus, text }));
        if (isPipelineTerminal({ transcriptStatus, noteStatus })) {
            logger.info('twilio:outboundcall generated note pipeline terminal', {
                callSid: poller.callSid,
                clinicId: poller.clinicId,
                transcriptStatus,
                noteStatus,
                attempts: attempt + 1
            });
            stopNotePolling();
            return;
        }
    } catch (err) {
        // Transient (network / 5xx / 404 while the server row is being inserted).
        // Persistent failures land in the cap below.
        logger.debug('twilio:outboundcall generated note poll attempt failed', {
            callSid: poller.callSid,
            attempt,
            error: err?.message
        });
    }

    if (!activeNotePoller || activeNotePoller !== poller || poller.cancelled) {
        return;
    }
    if (attempt + 1 >= NOTE_POLL_MAX_ATTEMPTS) {
        // Pipeline didn't settle in time — surface as FAILED so the user writes manually.
        logger.warn('twilio:outboundcall generated note polling exceeded max attempts', {
            callSid: poller.callSid,
            clinicId: poller.clinicId,
            attempts: attempt + 1
        });
        dispatch(generatedNoteUpdated({ transcriptStatus: 'FAILED', noteStatus: 'FAILED', text: null }));
        stopNotePolling();
        return;
    }
    poller.timeoutId = setTimeout(() => {
        pollGeneratedNoteOnce(attempt + 1);
    }, NOTE_POLL_INTERVAL_MS);
}

function startGeneratedNotePolling({ callSid, clinicId }) {
    if (!callSid || !clinicId) {
        return;
    }
    logger.info('twilio:outboundcall starting generated note polling', { callSid, clinicId });
    stopNotePolling();
    // Show the indicator immediately rather than waiting for the initial delay.
    dispatch(generatedNoteUpdated({ transcriptStatus: 'PENDING', noteStatus: 'PENDING', text: null }));
    activeNotePoller = { callSid, clinicId, timeoutId: null, cancelled: false };
    activeNotePoller.timeoutId = setTimeout(() => {
        pollGeneratedNoteOnce(0);
    }, NOTE_POLL_INITIAL_DELAY_MS);
}

// Lead time before token expiry at which the Twilio SDK fires `tokenWillExpire`.
// The SDK's default is 10s; 5min gives the network round-trip plenty of slack.
// Twilio's updateToken does not interrupt an in-progress call; the new token
// only refreshes the Device's signaling channel.
const TOKEN_REFRESH_LEAD_MS = 300 * 1000;

function resetLifecycleFlags() {
    localHangupRequested = false;
    wasAccepted = false;
    latestInputVolume = 0;
    cancelDuringStart = false;
}

function teardown() {
    latestInputVolume = 0;
    if (activeCall) {
        try {
            activeCall.removeAllListeners();
        } catch (e) {
            // ignore
        }
        activeCall = null;
    }
    if (device) {
        try {
            if (deviceChangeHandler && device.audio?.off) {
                device.audio.off('deviceChange', deviceChangeHandler);
            }
        } catch (e) {
            // ignore
        }
        deviceChangeHandler = null;
        try {
            device.destroy();
        } catch (e) {
            // ignore
        }
        device = null;
    }
}

async function publishAvailableDevices() {
    const { inputs, outputs } = await enumerateAudioDevices();
    dispatch(setAudioDevices({ inputDevices: inputs, outputDevices: outputs }));
    return { inputs, outputs };
}

// Apply a stored input deviceId to the live Device. Falls back to default and
// clears the stored value if the deviceId is no longer available. Returns the
// id that ended up active (string deviceId or null for default).
/**
 * @param {string | null} preferredId
 * @param {AudioDeviceInfo[]} availableInputs
 * @returns {Promise<string | null>}
 */
async function applyInputDevice(preferredId, availableInputs) {
    if (!device?.audio) {
        return null;
    }
    if (!preferredId) {
        return null;
    }
    const isAvailable = availableInputs.some((d) => d.deviceId === preferredId);
    if (!isAvailable) {
        writePreferredDeviceId(LS_INPUT_DEVICE_KEY, null);
        try {
            await device.audio.unsetInputDevice?.();
        } catch (e) {
            // ignore — falling back to default is best-effort
        }
        return null;
    }
    try {
        await device.audio.setInputDevice(preferredId);
        return preferredId;
    } catch (e) {
        writePreferredDeviceId(LS_INPUT_DEVICE_KEY, null);
        return null;
    }
}

/**
 * @param {string | null} preferredId
 * @param {AudioDeviceInfo[]} availableOutputs
 * @returns {Promise<string | null>}
 */
async function applySpeakerDevice(preferredId, availableOutputs) {
    if (!device?.audio || !OUTPUT_SELECTION_SUPPORTED) {
        return null;
    }
    if (!preferredId) {
        return null;
    }
    const isAvailable = availableOutputs.some((d) => d.deviceId === preferredId);
    if (!isAvailable) {
        writePreferredDeviceId(LS_SPEAKER_DEVICE_KEY, null);
        // Reset to a known-present sink. Twilio's speakerDevices.set accepts an
        // array of ids; an empty array doesn't reliably reset. 'default' is a
        // Chromium convention and not guaranteed, so fall back to the first
        // available output if it's missing.
        const fallback = availableOutputs.find((d) => d.deviceId === 'default') ?? availableOutputs[0];
        if (!fallback) {
            return null;
        }
        try {
            await device.audio.speakerDevices.set([fallback.deviceId]);
            await device.audio.ringtoneDevices.set([fallback.deviceId]);
        } catch (e) {
            return null;
        }
        // If we routed to the system 'default' sink, report null so the picker
        // shows "System default". Otherwise report the actual sink so the
        // picker reflects what audio is really playing through.
        return fallback.deviceId === 'default' ? null : fallback.deviceId;
    }
    try {
        await device.audio.speakerDevices.set([preferredId]);
        await device.audio.ringtoneDevices.set([preferredId]);
        return preferredId;
    } catch (e) {
        writePreferredDeviceId(LS_SPEAKER_DEVICE_KEY, null);
        return null;
    }
}

function notifyAudioFallback() {
    logger.warn('twilio:outboundcall saved audio device unavailable — falling back to default');
    dispatch(
        openSnackbar({
            open: true,
            message: 'Saved audio device unavailable — using system default',
            variant: 'alert',
            alert: { color: 'warning' }
        })
    );
}

async function initializeAudioFromPreferences() {
    if (!device?.audio) {
        return;
    }
    dispatch(setOutputSelectionSupported(OUTPUT_SELECTION_SUPPORTED));
    const { inputs, outputs } = await publishAvailableDevices();

    const savedInputId = readPreferredDeviceId(LS_INPUT_DEVICE_KEY);
    const savedSpeakerId = readPreferredDeviceId(LS_SPEAKER_DEVICE_KEY);

    const activeInputId = await applyInputDevice(savedInputId, inputs);
    const activeSpeakerId = await applySpeakerDevice(savedSpeakerId, outputs);

    dispatch(setSelectedInputDeviceId(activeInputId));
    dispatch(setSelectedOutputDeviceId(activeSpeakerId));

    logger.info('twilio:outboundcall audio devices initialized', {
        outputSelectionSupported: OUTPUT_SELECTION_SUPPORTED,
        inputs,
        outputs,
        savedInputId,
        savedSpeakerId,
        activeInputId,
        activeSpeakerId
    });

    const inputFellBack = !!savedInputId && activeInputId !== savedInputId;
    const speakerFellBack = !!savedSpeakerId && activeSpeakerId !== savedSpeakerId && OUTPUT_SELECTION_SUPPORTED;
    if (inputFellBack || speakerFellBack) {
        notifyAudioFallback();
    }
}

function attachDeviceChangeListener() {
    if (!device?.audio) {
        return;
    }
    deviceChangeHandler = async () => {
        if (!device?.audio) {
            return;
        }
        const { inputs, outputs } = await publishAvailableDevices();

        const previousInputId = readPreferredDeviceId(LS_INPUT_DEVICE_KEY);
        const previousSpeakerId = readPreferredDeviceId(LS_SPEAKER_DEVICE_KEY);
        const inputStillAvailable = !previousInputId || inputs.some((d) => d.deviceId === previousInputId);
        const speakerStillAvailable = !previousSpeakerId || outputs.some((d) => d.deviceId === previousSpeakerId);

        logger.info('twilio:outboundcall audio devices changed', {
            inputs,
            outputs,
            previousInputId,
            previousSpeakerId,
            inputStillAvailable,
            speakerStillAvailable
        });

        if (!inputStillAvailable || (!speakerStillAvailable && OUTPUT_SELECTION_SUPPORTED)) {
            const activeInputId = await applyInputDevice(previousInputId, inputs);
            const activeSpeakerId = await applySpeakerDevice(previousSpeakerId, outputs);
            dispatch(setSelectedInputDeviceId(activeInputId));
            dispatch(setSelectedOutputDeviceId(activeSpeakerId));
            logger.info('twilio:outboundcall audio devices re-applied after change', { activeInputId, activeSpeakerId });
            notifyAudioFallback();
        }
    };
    try {
        device.audio.on('deviceChange', deviceChangeHandler);
    } catch (e) {
        deviceChangeHandler = null;
    }
}

/** @param {import('@twilio/voice-sdk').Call} call */
function wireCallEvents(call) {
    call.on('ringing', () => {
        const callSid = call.parameters?.CallSid ?? null;
        logger.info('twilio:outboundcall ringing', { callSid });
        dispatch(callRinging({ callSid }));
    });
    // Twilio emits `volume` on the Call (~50Hz) once media is flowing. Cache
    // the input value so getInputVolume() returns it — there is no polled
    // device.audio.inputVolume property to read on demand.
    call.on('volume', (inputVolume) => {
        const v = Number(inputVolume);
        latestInputVolume = Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0;
    });
    call.on('accept', () => {
        wasAccepted = true;
        const callSid = call.parameters?.CallSid ?? null;
        logger.info('twilio:outboundcall accepted', { callSid });
        dispatch(callInProgress({ callSid, startedAt: Date.now() }));
    });
    // Twilio fires `disconnect` regardless of which side ended the call. We
    // distinguish via localHangupRequested (set by hangup() before invoking
    // the SDK) and wasAccepted (set on the `accept` event) to choose between
    // a silent dismissal and a lingering remoteEnded card.
    //
    // Twilio can emit multiple terminal events for the same call in the same
    // synchronous tick (e.g. `disconnect` followed by `cancel`). EventEmitter
    // snapshots its listener list per-emit, so teardown()'s removeAllListeners
    // does not dedupe events that were already queued. Guard on activeCall —
    // the first terminal event nulls it via teardown(), so any follow-up is a
    // no-op. Also resets the per-call flags so they don't leak forward if a
    // future startCall path skips resetLifecycleFlags.
    const handleTermination = () => {
        if (!activeCall) {
            return;
        }
        // Snapshot before teardown — slice still has clinicId here.
        const acceptedThisCall = wasAccepted;
        const callSid = call.parameters?.CallSid ?? null;
        const { clinicId } = store.getState().twilioOutboundCall;

        if (localHangupRequested) {
            logger.info('twilio:outboundcall ended locally', { callSid, wasAccepted: acceptedThisCall });
            dispatch(callEnded());
        } else {
            const message = wasAccepted ? 'Patient hung up' : 'Call ended without connecting';
            logger.info('twilio:outboundcall ended by remote', { callSid, wasAccepted: acceptedThisCall, message });
            dispatch(callRemoteDisconnect({ message }));
        }
        teardown();
        resetLifecycleFlags();

        // Only poll if the call connected — unanswered/rejected calls produce
        // no recording, so no transcript and no note.
        if (acceptedThisCall && callSid && clinicId) {
            startGeneratedNotePolling({ callSid, clinicId });
        }
    };
    call.on('disconnect', handleTermination);
    call.on('cancel', handleTermination);
    call.on('reject', handleTermination);
    call.on('error', (err) => {
        logger.error('twilio:outboundcall call error', {
            callSid: call.parameters?.CallSid ?? null,
            error: err?.message,
            code: err?.code
        });
        dispatch(callError(err?.message ?? 'Call error'));
        teardown();
    });
}

function clearFakeTimers() {
    fakeTimers.forEach(clearTimeout);
    fakeTimers = [];
}

function fakeTeardown() {
    clearFakeTimers();
    fakeActive = false;
}

function scheduleFake(fn, atMs) {
    fakeTimers.push(setTimeout(fn, atMs));
}

function fakeStartCall({ clinicId, patientId, patientSnapshot, toNumber }) {
    if (fakeActive) {
        return;
    }
    fakeActive = true;
    dispatch(callFetchingToken({ clinicId, patientId, patientSnapshot, toNumber }));

    // Seed the audio picker with fake devices so the picker UI is exercisable
    // in fake mode without a real Twilio Device. Saved IDs from a previous
    // real-mode session won't match these fake ids — filter so the Select
    // doesn't render an out-of-range value.
    const fakeInputDevices = [
        { deviceId: 'default', label: 'Default — System mic' },
        { deviceId: 'fake-mic-1', label: 'USB Headset Mic' }
    ];
    const fakeOutputDevices = [
        { deviceId: 'default', label: 'Default — System speaker' },
        { deviceId: 'fake-speaker-1', label: 'USB Headset' }
    ];
    dispatch(setOutputSelectionSupported(OUTPUT_SELECTION_SUPPORTED));
    dispatch(setAudioDevices({ inputDevices: fakeInputDevices, outputDevices: fakeOutputDevices }));

    const savedInputId = readPreferredDeviceId(LS_INPUT_DEVICE_KEY);
    const savedSpeakerId = readPreferredDeviceId(LS_SPEAKER_DEVICE_KEY);
    const validInputId = fakeInputDevices.some((d) => d.deviceId === savedInputId) ? savedInputId : null;
    const validSpeakerId = fakeOutputDevices.some((d) => d.deviceId === savedSpeakerId) ? savedSpeakerId : null;
    dispatch(setSelectedInputDeviceId(validInputId));
    dispatch(setSelectedOutputDeviceId(validSpeakerId));

    const step = FAKE_DELAY_MS;
    const fakeSid = `CAfake${Date.now()}`;

    scheduleFake(() => dispatch(callConnecting()), step);

    if (FAKE_OUTCOME === 'error') {
        scheduleFake(() => {
            dispatch(callError('[fake] Device error'));
            fakeTeardown();
        }, step * 2);
        return;
    }

    scheduleFake(() => dispatch(callRinging({ callSid: fakeSid })), step * 2);

    if (FAKE_OUTCOME === 'busy' || FAKE_OUTCOME === 'noanswer') {
        // Twilio surfaces these as a disconnect from the SDK's perspective; the
        // server-side status callback paints the OutboundCall row. From the
        // UI's standpoint these are remote-side terminations — the card lingers
        // with a label until the agent dismisses it.
        scheduleFake(() => {
            dispatch(callRemoteDisconnect({ message: 'Call ended without connecting' }));
            fakeTeardown();
        }, step * 4);
        return;
    }

    if (FAKE_OUTCOME === 'ring') {
        // Stay ringing forever until the user hangs up.
        return;
    }

    // Default: 'answer' — ring then bridge.
    scheduleFake(() => dispatch(callInProgress({ callSid: fakeSid, startedAt: Date.now() })), step * 4);
}

function fakeStartGeneratedNotePolling() {
    // Walk the stages so the indicator copy is exercisable in fake mode.
    dispatch(generatedNoteUpdated({ transcriptStatus: 'PENDING', noteStatus: 'PENDING', text: null }));
    setTimeout(() => {
        dispatch(generatedNoteUpdated({ transcriptStatus: 'STORED', noteStatus: 'GENERATING', text: null }));
    }, 1500);
    setTimeout(() => {
        dispatch(
            generatedNoteUpdated({
                transcriptStatus: 'STORED',
                noteStatus: 'READY',
                text: '[fake] Discussed medication adherence with patient. Reviewed dosing schedule and addressed side-effect concerns. Patient agreed to continue current regimen and confirmed follow-up in 2 weeks.'
            })
        );
    }, 3000);
}

function fakeHangup() {
    if (fakeActive) {
        const wasAcceptedFake = store.getState().twilioOutboundCall.status === 'inCall';
        dispatch(callEnded());
        fakeTeardown();
        if (wasAcceptedFake) {
            fakeStartGeneratedNotePolling();
        }
    }
}

function fakeSetMuted(muted) {
    if (fakeActive) {
        dispatch(setMutedAction(!!muted));
    }
}

/**
 * @param {{
 *   clinicId: number,
 *   patientId: number,
 *   patientSnapshot: { firstName?: string, lastName?: string, phoneNumber?: string, mrn?: string },
 *   toNumber: string | null
 * }} args
 */
export async function startCall({ clinicId, patientId, patientSnapshot, toNumber }) {
    if (FAKE_ENABLED) {
        fakeStartCall({ clinicId, patientId, patientSnapshot, toNumber });
        return;
    }

    if (device || activeCall || starting) {
        // A call is already in progress — refuse to start another.
        logger.warn('twilio:outboundcall refused — call already in progress', {
            clinicId,
            patientId,
            hasDevice: !!device,
            hasActiveCall: !!activeCall,
            starting
        });
        return;
    }
    starting = true;
    resetLifecycleFlags();

    dispatch(callFetchingToken({ clinicId, patientId, patientSnapshot, toNumber }));

    logger.info('twilio:outboundcall starting call', {
        clinicId,
        patientId,
        mrn: patientSnapshot.mrn,
        toNumber
    });

    let token;
    try {
        const response = await fetchTwilioAccessToken();
        logger.debug('twilio:outboundcall fetched access token');
        token = response.data.token;
    } catch (err) {
        starting = false;
        if (!cancelDuringStart) {
            logger.error('twilio:outboundcall failed to fetch access token', {
                error: err?.message
            });
            dispatch(callError(err?.message ?? 'Failed to fetch access token'));
        }
        return;
    }

    if (cancelDuringStart) {
        logger.info('twilio:outboundcall cancelled during token fetch', { clinicId, patientId });
        starting = false;
        return;
    }

    try {
        // Local reference so async callbacks (tokenWillExpire, scheduled
        // refresh) operate on the device they were registered for, not
        // whatever the module-level `device` happens to be after a teardown
        // or a subsequent startCall.
        const myDevice = new Device(token, { logLevel: 'warn', tokenRefreshMs: TOKEN_REFRESH_LEAD_MS });
        device = myDevice;
        myDevice.on('tokenWillExpire', async () => {
            try {
                const refreshed = await fetchTwilioAccessToken();
                // Bail if the device this refresh was scheduled for has been
                // torn down (or replaced by a subsequent call) while the token
                // fetch was in flight — otherwise we'd updateToken on a
                // stale/destroyed Device, or push the previous call's token
                // onto a new one.
                if (device !== myDevice) {
                    return;
                }
                logger.debug('twilio:outboundcall token refreshed', { patientId });
                myDevice.updateToken(refreshed.data.token);
            } catch (err) {
                // Surface but don't kill the call — the existing signaling
                // channel is still valid until the original token's hard
                // expiry.
                logger.error('twilio:outboundcall tokenWillExpire refresh failed', {
                    patientId,
                    error: err?.message,
                    code: err?.code
                });
            }
        });
        myDevice.on('error', (err) => {
            logger.error('twilio:outboundcall device error', {
                patientId,
                error: err?.message,
                code: err?.code
            });
            dispatch(callError(err?.message ?? 'Device error'));
            teardown();
        });

        // Apply persisted audio device preferences (and surface the device list
        // to the slice for the picker) before connecting. Must run after
        // `new Device(...)` because device.audio is only populated then.
        // Failures fall back to defaults silently so the call still proceeds.
        await initializeAudioFromPreferences();
        attachDeviceChangeListener();

        if (cancelDuringStart) {
            logger.info('twilio:outboundcall cancelled during audio init', { clinicId, patientId });
            teardown();
            return;
        }

        dispatch(callConnecting());
        logger.info('twilio:outboundcall connecting', { clinicId, patientId });
        const pendingCall = await myDevice.connect({ params: { PatientId: String(patientId) } });

        if (cancelDuringStart) {
            // Hangup arrived between `dispatch(callConnecting())` and the
            // connect promise resolving. Drop the orphaned Call without
            // wiring listeners; the terminal state has already been flipped
            // to `ended` by hangup().
            logger.info('twilio:outboundcall cancelled during connect', { clinicId, patientId });
            try {
                pendingCall.disconnect();
            } catch (e) {
                // ignore — teardown destroys the device anyway
            }
            teardown();
            return;
        }

        activeCall = pendingCall;
        wireCallEvents(activeCall);
    } catch (err) {
        if (cancelDuringStart) {
            // The reject is downstream of our own cancellation (device
            // destroyed mid-connect). UI already shows `ended`; don't
            // overwrite it with `error`.
            teardown();
            return;
        }
        logger.error('twilio:outboundcall failed to start call', {
            clinicId,
            patientId,
            error: err?.message,
            code: err?.code
        });
        dispatch(callError(err?.message ?? 'Failed to start call'));
        teardown();
    } finally {
        starting = false;
    }
}

export function hangup() {
    if (FAKE_ENABLED) {
        fakeHangup();
        return;
    }

    // Mark the upcoming `disconnect` event as locally initiated so the call
    // event handler dispatches callEnded (silent dismissal) rather than
    // callRemoteDisconnect (lingering card).
    localHangupRequested = true;
    if (activeCall) {
        logger.info('twilio:outboundcall hangup — disconnecting active call', {
            callSid: activeCall.parameters?.CallSid ?? null
        });
        try {
            activeCall.disconnect();
        } catch (e) {
            // ignore — disconnect handler will run teardown
        }
    } else if (starting) {
        // startCall is mid-flight (token fetch or device.connect()) and
        // there's no Call to disconnect yet. Flag the in-flight startCall to
        // tear down its own Device + pending Call once its awaited work
        // settles — calling teardown() here would destroy the Device out
        // from under an in-progress connect() and cause its promise to
        // reject through the catch.
        logger.info('twilio:outboundcall hangup — cancelling in-flight start');
        cancelDuringStart = true;
        dispatch(callEnded());
    } else {
        logger.info('twilio:outboundcall hangup — no active call');
        teardown();
        dispatch(callEnded());
    }
}

export function setMuted(muted) {
    if (FAKE_ENABLED) {
        fakeSetMuted(muted);
        return;
    }

    if (activeCall) {
        try {
            activeCall.mute(!!muted);
            logger.debug('twilio:outboundcall mute toggled', {
                callSid: activeCall.parameters?.CallSid ?? null,
                muted: !!muted
            });
            dispatch(setMutedAction(!!muted));
        } catch (e) {
            logger.warn('twilio:outboundcall mute failed', { error: e?.message, muted: !!muted });
        }
    }
}

// Picker UI calls these — each persists to localStorage on success and updates
// the slice. `null` means "browser/OS default" and clears the saved value.
// On SDK failure we clear the saved value (so a non-functional id doesn't get
// re-applied next call) and surface a snackbar so the user knows their pick
// didn't take.
function notifyAudioPickFailed(kind) {
    dispatch(
        openSnackbar({
            open: true,
            message: `Could not switch ${kind} — using system default`,
            variant: 'alert',
            alert: { color: 'warning' }
        })
    );
}

export async function setInputDevice(deviceId) {
    if (FAKE_ENABLED) {
        writePreferredDeviceId(LS_INPUT_DEVICE_KEY, deviceId || null);
        dispatch(setSelectedInputDeviceId(deviceId || null));
        return;
    }
    if (!device?.audio) {
        return;
    }
    try {
        if (deviceId) {
            await device.audio.setInputDevice(deviceId);
            writePreferredDeviceId(LS_INPUT_DEVICE_KEY, deviceId);
            dispatch(setSelectedInputDeviceId(deviceId));
            logger.info('twilio:outboundcall input device selected', { deviceId });
        } else {
            try {
                await device.audio.unsetInputDevice?.();
            } catch (e) {
                // ignore
            }
            writePreferredDeviceId(LS_INPUT_DEVICE_KEY, null);
            dispatch(setSelectedInputDeviceId(null));
            logger.info('twilio:outboundcall input device cleared (system default)');
        }
    } catch (err) {
        writePreferredDeviceId(LS_INPUT_DEVICE_KEY, null);
        dispatch(setSelectedInputDeviceId(null));
        notifyAudioPickFailed('microphone');
        logger.error('twilio:outboundcall setInputDevice failed', { deviceId, error: err?.message });
    }
}

export async function setSpeakerDevice(deviceId) {
    if (FAKE_ENABLED) {
        if (!OUTPUT_SELECTION_SUPPORTED) {
            return;
        }
        writePreferredDeviceId(LS_SPEAKER_DEVICE_KEY, deviceId || null);
        dispatch(setSelectedOutputDeviceId(deviceId || null));
        return;
    }
    if (!device?.audio || !OUTPUT_SELECTION_SUPPORTED) {
        return;
    }
    try {
        const target = deviceId || 'default';
        await device.audio.speakerDevices.set([target]);
        await device.audio.ringtoneDevices.set([target]);
        writePreferredDeviceId(LS_SPEAKER_DEVICE_KEY, deviceId || null);
        dispatch(setSelectedOutputDeviceId(deviceId || null));
        logger.info('twilio:outboundcall speaker device selected', { deviceId: deviceId || null, target });
    } catch (err) {
        writePreferredDeviceId(LS_SPEAKER_DEVICE_KEY, null);
        dispatch(setSelectedOutputDeviceId(null));
        notifyAudioPickFailed('speaker');
        logger.error('twilio:outboundcall setSpeakerDevice failed', { deviceId, error: err?.message });
    }
}

// Polled by the picker UI (~20Hz) into component-local state. Kept off the
// Redux slice to avoid 20 dispatches/sec while the menu is open.
export function getInputVolume() {
    if (FAKE_ENABLED) {
        // A gently animated value so the meter visibly moves in fake mode.
        return 0.2 + 0.4 * (0.5 + 0.5 * Math.sin(Date.now() / 250));
    }
    return latestInputVolume;
}

export function resetCallState() {
    if (FAKE_ENABLED) {
        fakeTeardown();
        dispatch(callCardDismissed());
        return;
    }

    teardown();
    dispatch(callCardDismissed());
}

// Patient page unmount/switch — abandon any pipeline tied to the prior patient
// so its READY dispatch can't leak onto the next patient's care note.
export function cancelGeneratedNotePolling() {
    stopNotePolling();
    dispatch(generatedNoteCleared());
}
